import type { Demand } from './types'
import { normalizeDemandStatus } from './demandStatus'
import { compareIsoDates, todayInBusinessZone } from '@/helper/valueChange.helper'

export type UnionDemandStatusTone = {
  colorPalette: 'yellow' | 'blue' | 'green' | 'red' | 'gray'
  label: string
}

type StatusFields = Pick<
  Demand,
  'status' | 'published' | 'isArchived' | 'periodEnd' | 'allocationsCount'
>

/**
 * A demand is finished once it is archived or its period has ended. Compared as ISO date strings
 * against today in the business time zone: `new Date('YYYY-MM-DD')` is UTC midnight and would move
 * the end date back a day in browsers west of UTC.
 */
export function isDemandFinished(demand: Pick<Demand, 'isArchived' | 'periodEnd'>) {
  if (demand.isArchived) return true

  const periodEnd = demand.periodEnd?.split('T')[0]
  if (!periodEnd) return false

  return compareIsoDates(periodEnd, todayInBusinessZone()) < 0
}

/**
 * Union-side status of a marketplace demand, shared by the marketplace table and the demand
 * detail page so both show the same state for the same demand (SIB-1087).
 *
 * Derived from the backend fields, not a backend enum: a finished demand (archived or past its
 * period) is either "Vermittelt" or not placeable, whatever its status or publication.
 * There is no CLOSED status: the backend enum has only the five values in `DemandStatus`, and a
 * finished demand ("Beendet") is the `isArchived` flag, handled by the first branch.
 */
export function getUnionDemandStatusTone(demand: StatusFields): UnionDemandStatusTone {
  const status = normalizeDemandStatus(demand.status)

  if (isDemandFinished(demand)) {
    if (status === 'COVERED') {
      return { colorPalette: 'green', label: 'Vermittelt' }
    }

    // Keeps "Rest" (partly placed) apart from nothing placed at all.
    if (status === 'PARTIALLY_COVERED' || (demand.allocationsCount ?? 0) > 0) {
      return { colorPalette: 'red', label: 'Nicht vermittelbarer Rest' }
    }

    return { colorPalette: 'red', label: 'Nicht vermittelbar' }
  }

  if (!demand.published) {
    return { colorPalette: 'yellow', label: 'Handlung erforderlich' }
  }

  if (status === 'PARTIALLY_COVERED') {
    return { colorPalette: 'yellow', label: 'Teilweise vermittelt' }
  }

  if (status === 'COVERED') {
    return { colorPalette: 'green', label: 'Vermittelt' }
  }

  if (status === 'OFFERED') {
    return { colorPalette: 'blue', label: 'Laufend' }
  }

  return { colorPalette: 'yellow', label: 'Handlung erforderlich' }
}
